<template>
  <ServicePage
    service-id="compute"
    :monthly-cost="monthlyCost"
    :item-count="canAdd ? 1 : 0"
    :can-add="canAdd"
    @add="addLines"
  >
    <div class="space-y-6">
      <!-- Instance family + search -->
      <div class="bg-white shadow-md rounded-lg border border-gray-200 overflow-hidden">
        <div class="p-5 border-b border-gray-200 flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
          <div>
            <h3 class="text-lg font-semibold text-gray-800">Instance type</h3>
            <p class="text-sm text-gray-500">
              {{ filteredInstances.length }} of {{ instances.length }} instance types
            </p>
          </div>
          <div class="w-full lg:w-72">
            <input
              v-model="search"
              type="search"
              class="form-input bg-white w-full"
              placeholder="Search instance types"
            />
          </div>
        </div>

        <div class="px-5 pt-4 flex flex-wrap gap-2">
          <button
            v-for="family in availableFamilies"
            :key="family.id"
            class="px-3 py-1.5 text-sm rounded-full border transition-colors"
            :class="
              activeFamily === family.id
                ? 'bg-indigo-600 border-indigo-600 text-white'
                : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
            "
            @click="activeFamily = family.id"
          >
            {{ family.label }}
          </button>
        </div>
        <p class="px-5 pt-2 text-xs text-slate-500">{{ activeFamilyCaption }}</p>

        <div class="p-5">
          <div class="overflow-x-auto rounded-lg border border-gray-200">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-slate-50">
                <tr>
                  <th class="px-4 py-3 w-10"></th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Instance
                  </th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    vCPUs
                  </th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Memory
                  </th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Storage
                  </th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hourly
                  </th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monthly
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr
                  v-for="instance in filteredInstances"
                  :key="instance.type"
                  class="cursor-pointer hover:bg-slate-50 transition-colors"
                  :class="selected && selected.type === instance.type ? 'bg-indigo-50' : ''"
                  @click="select(instance)"
                >
                  <td class="px-4 py-3">
                    <span
                      class="h-4 w-4 rounded-full flex items-center justify-center ring-2 ring-offset-2"
                      :class="
                        selected && selected.type === instance.type
                          ? 'bg-indigo-600 ring-indigo-300'
                          : 'bg-white ring-gray-300'
                      "
                    >
                      <span
                        v-if="selected && selected.type === instance.type"
                        class="h-1.5 w-1.5 rounded-full bg-white"
                      ></span>
                    </span>
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap">
                    <p class="text-sm font-medium text-gray-900">{{ instance.type }}</p>
                    <p v-if="instance.description" class="text-xs text-gray-500">
                      {{ instance.description }}
                    </p>
                    <span
                      v-if="instance.confidential"
                      class="inline-block mt-1 px-2 py-0.5 text-[10px] font-medium rounded bg-emerald-100 text-emerald-700"
                    >
                      SEV-SNP confidential
                    </span>
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-600 text-right">
                    {{ instance.specs.vCPUs ?? "—" }}
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-600 text-right whitespace-nowrap">
                    {{ instance.specs.memory ? `${instance.specs.memory} GB` : "—" }}
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-600 text-right whitespace-nowrap">
                    {{ instance.specs.storage ? `${instance.specs.storage} GB NVMe` : "—" }}
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-600 text-right whitespace-nowrap">
                    <span v-if="instance.onRequest">—</span>
                    <span v-else v-currency="instance.hourlyPrice">{{ instance.hourlyPrice }}</span>
                  </td>
                  <td class="px-4 py-3 text-sm font-medium text-gray-900 text-right whitespace-nowrap">
                    <span v-if="instance.onRequest" class="text-indigo-600">On request</span>
                    <span v-else v-currency="instance.price">{{ instance.price }}</span>
                  </td>
                </tr>
                <tr v-if="!filteredInstances.length">
                  <td colspan="7" class="px-4 py-8 text-center text-sm text-gray-500">
                    No instance types match your search.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Usage -->
      <div v-if="selected" class="bg-white shadow-md rounded-lg border border-gray-200 overflow-hidden">
        <div class="p-5 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-gray-800">Usage</h3>
          <p class="text-sm text-gray-500">{{ selected.type }} — {{ selected.spec || "individually configured" }}</p>
        </div>

        <div v-if="selected.onRequest" class="p-5">
          <div class="bg-indigo-50 border border-indigo-200 rounded p-4 text-sm text-indigo-800">
            <strong>{{ selected.type }}</strong> is quoted individually.
            {{ selected.description }}
            Add it to your estimate to keep it on the list — our team will price it with you.
          </div>
        </div>

        <div v-else class="p-5 space-y-5">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
            <NumberField
              v-model="instanceCount"
              label="Number of instances"
              :min="1"
              placeholder="1"
            />
            <div class="w-full">
              <label class="block font-medium text-slate-800 mb-1">Usage</label>
              <p class="text-xs text-slate-500 mb-2">How much of the month the instances run</p>
              <select v-model="usageMode" class="form-input bg-white w-full">
                <option value="full">Always on (730 hours/month)</option>
                <option value="hours">Hours per month</option>
                <option value="percent">Percentage of the month</option>
              </select>
            </div>
            <NumberField
              v-if="usageMode === 'hours'"
              v-model="usageHours"
              label="Hours per month"
              :min="0"
              :max="730"
              placeholder="730"
            />
            <NumberField
              v-else-if="usageMode === 'percent'"
              v-model="usagePercent"
              label="Utilization"
              unit="%"
              :min="0"
              placeholder="100"
            />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <NumberField
              v-model="blockStorage"
              label="Additional block storage per instance"
              unit="GB"
              :hint="blockStorageHint"
            />
            <NumberField
              v-model="ipCount"
              label="Public IPv4 addresses per instance"
              :hint="ipHint"
            />
          </div>

          <p class="text-xs text-slate-500">
            Instance storage ({{ selected.specs.storage || 0 }} GB NVMe) is included in the instance price.
          </p>
        </div>
      </div>

      <!-- Calculation preview -->
      <CostPreview v-if="line" :line="line" />
    </div>
  </ServicePage>
</template>

<script>
import { mapGetters } from "vuex";
import ServicePage from "@/components/config/ServicePage.vue";
import NumberField from "@/components/config/NumberField.vue";
import CostPreview from "@/components/config/CostPreview.vue";
import { configurable } from "@/mixins/configurable";
import {
  COMPUTE_FAMILIES,
  HOURS_PER_MONTH,
  byType,
  estimateLine,
  lineItem,
  normalizeCompute,
  priceOf,
  round,
} from "@/utils/pricing";

export default {
  name: "ComputeService",
  components: { ServicePage, NumberField, CostPreview },
  mixins: [configurable],
  data() {
    return {
      search: "",
      activeFamily: "dd1",
      selected: null,
      instanceCount: 1,
      usageMode: "full",
      usageHours: 730,
      usagePercent: 100,
      blockStorage: 0,
      ipCount: 0,
    };
  },
  computed: {
    ...mapGetters({ section: "pricing/section" }),

    instances() {
      return normalizeCompute(this.section("compute"));
    },
    availableFamilies() {
      return COMPUTE_FAMILIES.filter((family) =>
        this.instances.some((instance) => instance.family === family.id)
      );
    },
    activeFamilyCaption() {
      const family = COMPUTE_FAMILIES.find((f) => f.id === this.activeFamily);
      return family ? family.caption : "";
    },
    filteredInstances() {
      const term = this.search.trim().toLowerCase();
      return this.instances.filter((instance) => {
        const matchesFamily = term ? true : instance.family === this.activeFamily;
        const matchesSearch =
          !term ||
          instance.type.toLowerCase().includes(term) ||
          instance.spec.toLowerCase().includes(term);
        return matchesFamily && matchesSearch;
      });
    },

    blockStoragePrice() {
      return priceOf(byType(this.section("storage.block"), "standard"));
    },
    blockStorageHint() {
      return `Block storage volumes attached to the instance, charged separately from the included NVMe storage.`;
    },
    ipPrice() {
      return priceOf(byType(this.section("network.ip"), "month12"));
    },
    ipHint() {
      return `Charged at the discounted 12-month commitment rate.`;
    },

    /** Fraction of the month the instance runs, 0..1+ */
    usageFactor() {
      if (this.usageMode === "hours") {
        return Math.min(Number(this.usageHours) || 0, HOURS_PER_MONTH) / HOURS_PER_MONTH;
      }
      if (this.usageMode === "percent") {
        return Math.min(Number(this.usagePercent) || 0, 100) / 100;
      }
      return 1;
    },
    usageLabel() {
      if (this.usageMode === "full") return "730 hours/month";
      if (this.usageMode === "hours") return `${round(this.usageHours)} hours/month`;
      return `${round(this.usagePercent)}% of the month`;
    },

    /** Overrides the mixin: this page configures exactly one line. */
    lines() {
      if (!this.line) return [];
      return this.line.monthlyCost > 0 || this.line.onRequest ? [this.line] : [];
    },

    line() {
      if (!this.selected) return null;

      const quantity = Math.max(1, Math.floor(Number(this.instanceCount) || 1));

      if (this.selected.onRequest) {
        return estimateLine({
          key: "compute",
          serviceId: "compute",
          name: `Compute ${this.selected.type}`,
          description: this.selected.description || "Individually quoted",
          config: { type: this.selected.type, onRequest: true },
          items: [],
          quantity: 1,
          onRequest: true,
        });
      }

      const items = [
        lineItem({
          label: `Instance ${this.selected.type}`,
          detail: `${this.selected.spec} — ${this.usageLabel}`,
          quantity: round(this.usageFactor, 4),
          unit: "month",
          unitPrice: this.selected.price,
          cost: this.selected.price * this.usageFactor,
        }),
      ];

      const storage = Number(this.blockStorage) || 0;
      if (storage > 0) {
        items.push(
          lineItem({
            label: "Block storage",
            detail: `${storage} GB`,
            quantity: storage,
            unit: "GB/month",
            unitPrice: this.blockStoragePrice,
          })
        );
      }

      const ips = Number(this.ipCount) || 0;
      if (ips > 0) {
        items.push(
          lineItem({
            label: "Public IPv4",
            detail: `${ips} address(es), 12-month commitment`,
            quantity: ips,
            unit: "month",
            unitPrice: this.ipPrice,
          })
        );
      }

      return estimateLine({
        key: "compute",
        serviceId: "compute",
        name: `Compute ${this.selected.type}`,
        description: this.selected.spec,
        config: {
          type: this.selected.type,
          spec: this.selected.spec,
          usageMode: this.usageMode,
          usageHours: this.usageHours,
          usagePercent: this.usagePercent,
          blockStorage: storage,
          ipCount: ips,
        },
        items,
        quantity,
      });
    },
  },
  watch: {
    instances: {
      immediate: true,
      handler(list) {
        if (!this.selected && list.length) {
          const first = list.find((instance) => !instance.onRequest) || list[0];
          this.select(first);
        }
      },
    },
  },
  methods: {
    select(instance) {
      this.selected = instance;
      if (instance && instance.family !== this.activeFamily && !this.search) {
        this.activeFamily = instance.family;
      }
    },
  },
};
</script>
